/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import styled from 'styled-components';
import {Autocomplete, AutocompleteProps} from '@mui/material';
import appTheme from '../theme/appTheme.json';
import {AutofillWrapper} from './AutofillWrapper';

// Wrapper to manage border radius of autocomplete dropdown. The MUI autocomplete will try and fit the dropdown
// above or below the input based on available space. This wrapper will adjust the border of the text input and
// the dropdown based on the placement of the dropdown.
const StyledAutocompleteWrapper = styled.div`
    // Adjustments to the text input. This selects the text input that has a dropdown open
    // if the dropdown is above the text input.
    div[aria-owns]:has(+ div[data-popper-placement='top']) .MuiOutlinedInput-notchedOutline {
        border-top-right-radius: 0;
        border-top-left-radius: 0;
    }

    // Same as above, but if the dropdown is below the text input.
    div[aria-owns]:has(+ div[data-popper-placement='bottom']) .MuiOutlinedInput-notchedOutline {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
    }

    // Adjustments to the dropdown
    .MuiAutocomplete-paper {
        border: 1px solid #18ECFC;
        border-radius: 8px;
    }

    // Adjustments to the dropdown if it is above the text input.
    div[data-popper-placement='top'] {
        .MuiAutocomplete-paper {
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
            border-bottom: 0;
        }
    }

    // Same as above, but if the dropdown is below the text input.
    div[data-popper-placement='bottom'] {
        .MuiAutocomplete-paper {
            border-top-right-radius: 0;
            border-top-left-radius: 0;
            border-top: 0;
        }
    }
`;

const StyledAutocomplete: typeof Autocomplete = styled(Autocomplete)`
  // aria-owns is present when the autocomplete list is displayed
  &[aria-owns] .MuiOutlinedInput-notchedOutline {
    border-color: #18ecfc !important;
  }

  & .Mui-error > .MuiOutlinedInput-notchedOutline {
    border-color: #fa7272 !important;
  }
    
  & .Mui-disabled > .MuiOutlinedInput-input {
    color: ${appTheme.disabledTextColor};
    -webkit-text-fill-color: ${appTheme.disabledTextColor};
  }

  // ensure disabled border stays gray on hover
  & .Mui-disabled .MuiOutlinedInput-notchedOutline,
  & .Mui-disabled:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${appTheme.disabledTextColor} !important;
  }

  & .MuiAutocomplete-inputRoot {
    color: white;
    font-family: ${appTheme.appFontFamily};
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    background-color: #141d24;

    border-radius: 5px;
    font-size: 16px;

    // change border color
    & .MuiOutlinedInput-notchedOutline {
      border-color: #9fafbc;
      border-width: 1px;
    }

    // change border color on hover
    &:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline {
      border-color: white;
    }

    & .MuiChip-label {
      font-family: ${appTheme.appFontFamily};
      font-size: 16px;
      border-radius: 4px;
      background: rgba(159, 175, 188, 0.1);
      color: ${appTheme.primaryTextColor};
      text-align: left;
      font-feature-settings: 'clig' off, 'liga' off;
    }

    & .MuiChip-deleteIcon {
      color: #9fafbc;
    }
  }
`;

export const BeamAutocomplete = (params: AutocompleteProps<any, any, any, any>) => {
  return (
    <AutofillWrapper>
      <StyledAutocompleteWrapper>
        <StyledAutocomplete
          {...params}
          ListboxProps={{
            style: {
              overflow: 'auto',
            },
          }}
          slotProps={{
            paper: {
              sx: {
                '&:not(:empty)': {
                  background: '#1C262F',
                  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.20)',
                },
                'ul::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '& li': {
                  color: '#9fafbc',
                },
                "& li[aria-selected='true']": {
                  color: 'white',
                },
                "& li.Mui-focused:hover,li.MuiAutocomplete-option[aria-selected='true'],li.MuiAutocomplete-option[aria-selected='true'].Mui-focused":
                  {
                    backgroundColor: '#32424E',
                  },
              },
            },
            popper: {
              disablePortal: true,
            },
            ...params.slotProps,
          }}
        />
      </StyledAutocompleteWrapper>
    </AutofillWrapper>
  );
};
